import React, { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { FileText, Shield, BarChart3, LogOut, Menu, X, Zap, User, Settings, Key, TrendingUp, Webhook } from 'lucide-react';
import { analyticsAPI, setAuthToken } from '../services/api';
import LanguageSelector from '../components/LanguageSelector';
import NotificationCenter from '../components/NotificationCenter';
import TrialBanner from '../components/trial/TrialBanner';
import { useTranslation } from '../hooks/useTranslation';

const DashboardLayout = () => {
  const { user, logout, token } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [stats, setStats] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [showTrialBanner, setShowTrialBanner] = useState(true);

  useEffect(() => {
    if (token) {
      setAuthToken(token);
      loadStats();
    }
  }, [token]);

  const loadStats = async () => {
    try {
      const response = await analyticsAPI.getOverview();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const menuItems = [
    { path: '/dashboard', icon: BarChart3, label: t('nav.overview'), exact: true },
    { path: '/dashboard/smartfacture', icon: FileText, label: 'SmartFacture AI' },
    { path: '/dashboard/metrics', icon: TrendingUp, label: t('nav.metrics') },
  ];

  const userMenuItems = [
    { path: '/dashboard/profile', icon: User, label: t('nav.profile') },
    { path: '/dashboard/security', icon: Shield, label: 'Sécurité' },
    { path: '/dashboard/api-keys', icon: Key, label: t('nav.apiKeys') },
    { path: '/dashboard/webhooks', icon: Webhook, label: 'Webhooks' },
    { path: '/dashboard/settings', icon: Settings, label: t('nav.settings') },
  ];

  const isActiveRoute = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 transition-colors">
      {/* Trial Banner */}
      {showTrialBanner && <TrialBanner onClose={() => setShowTrialBanner(false)} />}
      
      {/* Top Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border-b border-slate-200 dark:border-slate-800" style={{ top: showTrialBanner ? 'auto' : 0 }}>
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center space-x-4">
              <button
                className="md:hidden p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="h-5 w-5 text-slate-900 dark:text-white" /> : <Menu className="h-5 w-5 text-slate-900 dark:text-white" />}
              </button>
              <Link to="/dashboard" className="flex items-center">
                <img src="/smartfacture-logo.png" alt="SmartFacture AI" className="h-9 w-auto" />
              </Link>
            </div>
            
            <div className="flex items-center space-x-3">
              <NotificationCenter />
              <LanguageSelector />
              
              {/* User Menu */}
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center space-x-3 px-3 py-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  data-testid="user-menu-button"
                >
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt="Avatar" className="h-8 w-8 rounded-full object-cover" />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-gradient-to-r from-emerald-600 to-emerald-700 flex items-center justify-center">
                      <span className="text-white text-sm font-bold">
                        {user?.first_name?.[0] || user?.full_name?.[0] || user?.email?.[0]?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  )}
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium text-slate-900 dark:text-white">{user?.full_name || user?.email}</p>
                    <p className="text-xs text-slate-500 dark:text-slate-400">{user?.company_name || 'Personal'}</p>
                  </div>
                </button>
                
                {/* Dropdown */}
                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-2 animate-in fade-in slide-in-from-top-2 duration-200">
                    {userMenuItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={() => setUserMenuOpen(false)}
                          className="flex items-center space-x-3 px-4 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors"
                          data-testid={`user-menu-${item.path.split('/').pop()}`}
                        >
                          <Icon className="h-4 w-4" />
                          <span>{item.label}</span>
                        </Link>
                      );
                    })}
                    <div className="border-t border-slate-200 dark:border-slate-700 my-2"></div>
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-3 px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors w-full text-left"
                      data-testid="logout-btn"
                    >
                      <LogOut className="h-4 w-4" />
                      <span>{t('nav.logout')}</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </nav>

      <div className="flex pt-16">
        {/* Sidebar */}
        <aside 
          className={`
            fixed md:static inset-y-0 left-0 z-40 w-64 bg-white dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800
            transform transition-transform duration-300 ease-in-out
            ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          `}
          style={{ top: '64px', height: 'calc(100vh - 64px)' }}
        >
          <div className="p-6 space-y-2 overflow-y-auto h-full">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = isActiveRoute(item.path, item.exact);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`
                    flex items-center space-x-3 px-4 py-3 rounded-lg transition-all group
                    ${isActive 
                      ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white shadow-md' 
                      : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                    }
                  `}
                  data-testid={`nav-${item.path.split('/').pop() || 'overview'}`}
                >
                  <Icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-slate-400 dark:text-slate-500 group-hover:text-emerald-600'}`} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}

            {stats && (
              <div className="pt-6 mt-6 border-t border-slate-200 dark:border-slate-700">
                <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">Quick Stats</h3>
                <div className="space-y-3">
                  <Card className="p-4 bg-gradient-to-br from-emerald-50 to-lime-50 border-emerald-100">
                    <p className="text-xs text-slate-600 font-medium">SmartFacture AI</p>
                    <p className="text-2xl font-bold text-emerald-600" style={{fontFamily: "'Outfit', sans-serif"}}>
                      {stats.smartfacture?.total_invoices || 0}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">factures créées</p>
                  </Card>
                </div>
              </div>
            )}
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 md:p-8 overflow-x-hidden min-h-[calc(100vh-64px)] bg-slate-50 dark:bg-slate-950">
          <Outlet />
        </main>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/20 dark:bg-black/40 z-30 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
